import { useEffect, useMemo } from "react";
import { Teksten } from "../../../content";
import { IdentifierWithChildren } from "../../../factories/waardelijst";
import { Identifier } from "../../../models";
import { Bestand, Documenthandelingen, Versies } from "../../../models/document-metadata";
import { IListValidator } from "../../../models/validator";
import { useAppSelector } from "../../../store/hooks";
import {
  addForm,
  addReason,
  cancelFileChange,
  clearDocumentState,
  conceptDate,
  create,
  fetchForm,
  setMode,
  setOnderwerpenRonl,
  update,
  updateDocumentHandelingen,
  updateErrorReviewed,
  updateZichtbaarheidDate,
  updateZichtbaarheidTime,
} from "../../../store/reducers/document-metadata";
import { selectStepper } from "../../../store/selectors";
import { formatDate, formatStringToDate, stringToDate, timeFormatter } from "../../../utils/DateFormatter";
import { CreateFormConfig } from "../../../utils/DocumentFormConfig";
import { UseFormContextDocuments, UseFormContextStatesDocument } from "../../../utils/DocumentFormContext";
import { isDpcHoofdDoc, isFormDisabled } from "../../../utils/FormHelper";
import { isPublished } from "../../../utils/Metadata";
import {
  filterActievePublishers,
  getOrganisatiesForpubicatiebestemming,
  useOrganisaties,
} from "../../../utils/OrganisatiesHelper";
import { StringSanitizer } from "../../../utils/StringSanitizer";
import { RequiredListValidator } from "../../../validations/requiredValidator";
import { CheckboxAtom, ContainerAtom, SpacerAtom } from "../../atoms";
import {
  ButtonMolecule,
  DocumenthandelingMolecule,
  FoldableMolecule,
  FormMolecule,
  IconButtonMolecule,
  LabelMolecule,
  PopupMolecule,
  SelectDropdownMolecule,
  TextInputMolecule,
  TooltipMolecule,
  UitgesteldPublicerenToggleMolecule,
} from "../../molecules";
import { FormFieldRenderer } from "../../molecules/formfields";

export function DocumentFormulierOrganism() {
  const {
    factories,
    dispatch,
    root,
    formStates,
    formState,
    formStateSetIndex,
    formStateIndex,
    documentError,
    localValidationError,
    mode,
    authState,
    id,
    hasDocumentErrors,
    hasErrorReviewed,
  } = UseFormContextDocuments();

  const fcd = UseFormContextStatesDocument();
  const { organisaties, publicerendePartijen } = useOrganisaties();
  const AuthOrganisationsList = factories.orgMemberships.create();
  const stateStepper = useAppSelector(selectStepper);
  const { flow } = stateStepper;
  const requiredRefs: HTMLInputElement[] = [];

  useMemo(() => {
    const lijst = filterActievePublishers(publicerendePartijen, AuthOrganisationsList);
    fcd.setActievePublishers(lijst);
    return lijst;
  }, [publicerendePartijen]);

  useEffect(() => {
    factories.waardelijsten.getRedenIntrekkingVervanging().then(fcd.setRedenIntrekkingVervanging);
    factories.waardelijsten.getRonlOnderwerpen().then(fcd.setRonlOnderwerpen);
    factories.waardelijsten.getInformatiecategorieen().then(fcd.setInformatiecategorieen);
    factories.waardelijsten.getTaal().then(fcd.setTaal);
  }, []);

  useEffect(() => {
    if (!id) {
      handleCreateMode();
      return;
    }

    handleUpdateMode(id);
  }, [dispatch, id]);

  useEffect(() => {
    const uitgesteldPublicatieDate = formState?.meta.document.zichtbaarheidsdatumtijd
      ? new Date(formState?.meta.document.zichtbaarheidsdatumtijd ?? "").toJSON()
      : undefined;
    const isUitgesteld = !!(
      uitgesteldPublicatieDate !== undefined && uitgesteldPublicatieDate !== conceptDate.toJSON()
    );
    const isNotIntrekking = formState?.meta?.plooiIntern?.publicatiestatus !== "intrekking";
    const isNotPending = formState?.meta?.plooiIntern?.publicatiestatus !== "pending";
    const hasMatchingDate = uitgesteldPublicatieDate === conceptDate?.toJSON();
    const isConcept = !!(isNotIntrekking && isNotPending && uitgesteldPublicatieDate !== undefined && hasMatchingDate);

    fcd.setUitgesteldPubliceren(isUitgesteld);
    fcd.setConcept(isConcept);
    if (formState?.meta.document.zichtbaarheidsdatumtijd && isUitgesteld) {
      fcd.setCurrentPublishDate(stringToDate(formState?.meta.document.zichtbaarheidsdatumtijd));
      fcd.setCurrentPublishTime(timeFormatter(stringToDate(formState?.meta.document.zichtbaarheidsdatumtijd)));
    }

    if (formState?.meta.document.documenthandelingen) {
      const docDatums = formState?.meta.document.documenthandelingen.map((handeling: Documenthandelingen) =>
        formatDate(stringToDate(handeling.atTime))
      );
      fcd.setDocumentDatums(docDatums);
    }
  }, [formState?.meta.document.zichtbaarheidsdatumtijd, formState?.meta.document.documenthandelingen]);

  useEffect(() => {
    if (mode !== "update") return;

    const selectedOnderwerpen = formState?.meta.document.classificatiecollectie.onderwerpenRonl ?? [];
    if (selectedOnderwerpen.length === 0 || fcd.ronlOnderwerpen.length === 0) return;

    const normalizeLabel = (label?: string) => label?.trim().toLowerCase() ?? "";

    const onderwerpenById = new Set(fcd.ronlOnderwerpen.map((o) => o.id));

    const onderwerpenByNormalizedLabel = new Map(fcd.ronlOnderwerpen.map((o) => [normalizeLabel(o.label), o]));

    const normalizedOnderwerpen = selectedOnderwerpen.map((onderwerp) => {
      if (onderwerpenById.has(onderwerp.id)) return onderwerp;
      return onderwerpenByNormalizedLabel.get(normalizeLabel(onderwerp.label)) ?? onderwerp;
    });

    const hasChanged = normalizedOnderwerpen.some(
      (o, i) => o.id !== selectedOnderwerpen[i].id || o.label !== selectedOnderwerpen[i].label
    );

    if (hasChanged) {
      dispatch(setOnderwerpenRonl(normalizedOnderwerpen));
    }
  }, [dispatch, mode, fcd.ronlOnderwerpen, formState?.meta.document.classificatiecollectie.onderwerpenRonl]);

  useEffect(() => {
    if (hasDocumentErrors()) {
      requiredRefs.forEach((element: HTMLInputElement | HTMLButtonElement, index: number) => {
        if (index === 0) element.click();
        element.focus();
        element.blur();
      });
    }
  }, [documentError.indexList]);

  const handleCreateMode = () => {
    if (mode === "update") dispatch(setMode("create"));

    if (formStates.length > 0 && !hasDocumentErrors()) return;

    dispatch(clearDocumentState());
    dispatch(addForm());
  };

  const handleUpdateMode = (id: string) => {
    dispatch(setMode("update"));

    if (hasDocumentErrors() && !hasErrorReviewed()) {
      dispatch(updateErrorReviewed(true));
      return;
    }

    dispatch(clearDocumentState());
    dispatch(fetchForm(id));
  };

  if (!formState) {
    return <></>;
  }
  const { documenthandelingen, publisher, publicatiebestemming, geldigheid } = formState.meta.document;

  const isNew = !formState.meta.document.pid;
  const hoofdDoc = formStates[formStateSetIndex][0];
  const publicatieStatus = formState.meta.plooiIntern?.publicatiestatus ?? "";
  const ksmOrganisaties = getOrganisatiesForpubicatiebestemming(
    publicatiebestemming,
    organisaties,
    publicerendePartijen
  );
  const meta = root.data?.[0]?.[0]?.meta;
  const published = (meta && isPublished(meta)) || (hasDocumentErrors() && !localValidationError);
  const createFileName = () => formState.file?.name;
  const isValidFileLabel = (label?: string) => label === "file" || label === "pdf" || label === "zip";
  const isFileExtension = (bestandsnaam?: string) => !!bestandsnaam && /\.(pdf|zip)$/i.test(bestandsnaam);

  const isDocumentBestand = (b: Bestand) => isValidFileLabel(b.label) || (!b.label && isFileExtension(b.bestandsnaam));
  const getFileFromVersion = (version?: Versies) => version?.bestanden?.find(isDocumentBestand);

  const editFileName = () => {
    if (!formState.file?.name && (formState.meta.document.weblocatie || formState.fileExplicitlyRemoved))
      return undefined;

    if (isNew) {
      return formState.file?.name;
    }

    if (formState.file?.name) {
      return formState.file.name;
    }
    const latestVersionWithFile = formState.meta.versies
      ?.slice()
      .reverse()
      .find((v: Versies) => getFileFromVersion(v));

    if (!latestVersionWithFile) return undefined;

    return getFileFromVersion(latestVersionWithFile)?.bestandsnaam ?? "Onbekende bestandsnaam.";
  };

  const actievePublisher: boolean = publicerendePartijen.some(
    (organisatie: IdentifierWithChildren) => organisatie.id === publisher.id && organisatie.label === publisher.label
  );
  const plooiPublicatieStatus = root.data?.[0]?.[0]?.meta?.plooiIntern?.publicatiestatus ?? "";

  const disableForm: boolean = isFormDisabled(actievePublisher, publicatieStatus, plooiPublicatieStatus);

  const dpcHoofdDoc: boolean = isDpcHoofdDoc(hoofdDoc.meta.document.pid);

  const defaultNewHandeling = {
    soortHandeling: {
      id: "https://identifier.overheid.nl/tooi/def/thes/kern/c_641ecd76",
      label: "vaststelling",
    },
    atTime: new Date().toJSON(),
    wasAssociatedWith: {
      id: authState.organisatie[0].id,
      label: authState.organisatie[0].label,
    },
  };

  const documenthandelingenElement = (validation?: IListValidator[]) => {
    let keyIndex = 0;
    return (
      <>
        <LabelMolecule
          label={Teksten.createScreen.documenthandeling.label}
          id={Teksten.createScreen.documenthandeling.label + "_label"}
          tooltip={Teksten.createScreen.documenthandeling.tooltip}
          required
        />
        {documenthandelingen.map((_: any, index: number) => {
          keyIndex++;
          return (
            <DocumenthandelingMolecule
              key={keyIndex}
              index={index}
              documenthandelingen={formState.meta.document.documenthandelingen}
              disableForm={disableForm}
              dispatch={dispatch}
              requiredRefs={requiredRefs}
              requiredDropdownValidations={validation!}
              factories={factories}
              ksmOrganisaties={ksmOrganisaties}
              documentDatums={fcd.documentDatums}
              setDocumentDatums={fcd.setDocumentDatums}
            />
          );
        })}
        <SpacerAtom space={4} />
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <IconButtonMolecule
            disabled={disableForm}
            text="Voeg handeling toe"
            title="Voeg een extra documenthandeling toe"
            icon={"icon-plus-shape"}
            id={"add_documenthandeling_button"}
            type={"blue"}
            size={"medium"}
            onClick={() => {
              fcd.setDocumentDatums([...fcd.documentDatums, formatDate(new Date())]);
              dispatch(
                updateDocumentHandelingen([...formState.meta.document.documenthandelingen, defaultNewHandeling])
              );
            }}
          />
        </div>
      </>
    );
  };

  const uploadInfoElement = () => {
    const publicatieStatus = formState.meta.plooiIntern?.publicatiestatus ?? "";
    const stringSanitizer = new StringSanitizer();

    if (!formState.meta.document.pid) return null;

    const documentstatus = fcd.concept ? "Concept" : stringSanitizer.sanitizePublicatieStatus(publicatieStatus);
    const aanleverdatum = formState.meta.versies?.at(0)?.openbaarmakingsdatum
      ? formatStringToDate(formState.meta.versies?.at(0)?.openbaarmakingsdatum ?? "")
      : "";
    const laatstBewerkt = formState.meta.versies?.at(0)?.mutatiedatumtijd
      ? formatStringToDate(formState.meta.versies?.at(0)?.mutatiedatumtijd ?? "")
      : "";
    return (
      <ContainerAtom type="row">
        <TextInputMolecule
          disabled
          id={Teksten.createScreen.documentstatus.id}
          value={documentstatus}
          label={Teksten.createScreen.documentstatus.label}
          tooltip={Teksten.createScreen.documentstatus.tooltip}
        />
        <TextInputMolecule
          disabled
          id={Teksten.createScreen.aanleverdatum.id}
          value={aanleverdatum}
          label={Teksten.createScreen.aanleverdatum.label}
          tooltip={Teksten.createScreen.aanleverdatum.tooltip}
        />
        <TextInputMolecule
          disabled
          id={Teksten.createScreen.laatstBewerkt.id}
          value={laatstBewerkt}
          label={Teksten.createScreen.laatstBewerkt.label}
          tooltip={Teksten.createScreen.laatstBewerkt.tooltip}
        />
      </ContainerAtom>
    );
  };

  const uitgesteldPublicerenToggleElement = () => {
    return (
      <UitgesteldPublicerenToggleMolecule
        uitgesteldPubliceren={fcd.uitgesteldPubliceren}
        disabled={formStateIndex !== 0 || published || disableForm}
        geldigheid={geldigheid}
        formState={formState}
        fcd={fcd}
      />
    );
  };

  const toggleConcept = () => {
    fcd.setConcept(!fcd.concept);
    fcd.setUitgesteldPubliceren(false);

    if (fcd.concept) {
      dispatch(updateZichtbaarheidDate(undefined));
    } else {
      let date = conceptDate;
      dispatch(updateZichtbaarheidDate(date.toJSON()));
      dispatch(updateZichtbaarheidTime(date.toTimeString()));
    }
  };

  const conceptToggleElement = () => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <CheckboxAtom
          disabled={formStateIndex !== 0 || published || disableForm}
          id={Teksten.createScreen.concept.id}
          checked={fcd.concept}
          onClick={() => toggleConcept()}
          label={Teksten.createScreen.concept.label}
        />

        <TooltipMolecule
          id={Teksten.createScreen.concept_tooltip.id}
          content={Teksten.createScreen.concept_tooltip.tooltip}
        />
      </div>
    );
  };

  const onUpload = () => {
    if (mode === "create") {
      dispatch(create());
      return;
    }

    dispatch(update(true));
  };

  const rightButton = () => {
    return (
      <ButtonMolecule
        text={mode === "create" ? "Document(en) publiceren" : "Wijzigingen publiceren"}
        id={"document_publiceren"}
        title={"Start hier met het publiceren van uw documenten."}
        onClick={onUpload}
        type={"primary"}
        disabled={disableForm}
      />
    );
  };

  const leftButton = () => {
    if (flow === "Dossier")
      return (
        <ButtonMolecule
          text={"Dossier aanmaken annuleren "}
          id={"dossier_annuleren"}
          title={"Dossier aanmaken annuleren "}
          onClick={() => {}}
          type={"default"}
          disabled={disableForm}
        />
      );
  };

  const updatePopup = () => {
    return (
      (fcd.popupUpdate || fcd.popupHerindexeren) && (
        <PopupMolecule
          title={fcd.popupHerindexeren ? "Reden van herindexeren" : "Reden voor vervangen"}
          text={`U staat op het punt dit document te ${fcd.popupHerindexeren ? "herindexeren" : "vervangen"}`}
          cancelButton={{
            text: "Annuleer",
            onClick: () => {
              dispatch(
                cancelFileChange({
                  weblocatieVoorWijziging: fcd.weblocatieVoorWijziging,
                  hasChangedMetaVoorWijziging: fcd.hasChangedMetaVoorWijziging,
                  bestandenVoorWijziging: fcd.bestandenVoorWijziging,
                })
              );
              fcd.setPopupUpdate(false);
              fcd.setPopupHerindexeren(false);
              fcd.setRedenUpdate(undefined);
            },
          }}
          okeButton={{
            text: "Bevestigen",
            disabled: !fcd.redenUpdate,
            onClick: () => {
              dispatch(
                addReason({
                  pid: formState.meta.document.pid as string,
                  reasonId: fcd.redenUpdate?.id as string,
                })
              );

              fcd.setPopupHerindexeren(false);
              fcd.setPopupUpdate(false);
              fcd.setRedenUpdate(undefined);
            },
          }}
          extraInfo={
            <SelectDropdownMolecule
              disabled={disableForm}
              key={Teksten.createScreen.reden.id}
              onUpdated={(cb: Identifier) => fcd.setRedenUpdate(cb)}
              selected={fcd.redenUpdate ? [fcd.redenUpdate] : []}
              tekst={{
                ...Teksten.createScreen.reden,
                placeholder: `${fcd.popupHerindexeren ? "Voer reden voor herindexatie in" : Teksten.createScreen.reden.placeholder}`,
              }}
              categories={factories.dropdownInfo.generateCheckboxListSelect(
                fcd.redenIntrekkingVervanging,
                fcd.redenUpdate
              )}
              singleSelect
              required
              search={false}
              validations={[new RequiredListValidator()]}
            />
          }
        />
      )
    );
  };

  const fileName = mode === "create" ? createFileName() : editFileName();
  const fileSelected = formState.file ?? editFileName();

  const formConfig = CreateFormConfig({
    dispatch,
    disableForm,
    fcd,
    requiredRefs,
    mode,
    factories,
    data: formState.meta,
    formState,
    fileName,
    fileSelected,
    originalWeblocatieUrl: formState.originalWeblocatieUrl,
    ksmOrganisaties,
    ronlOnderwerpen: fcd.ronlOnderwerpen,
    formStateIndex,
    hoofdDoc,
    authState,
    uploadInfoElement,
    documenthandelingenElement,
    conceptToggleElement,
    uitgesteldPublicerenToggleElement,
  });

  return (
    <>
      {updatePopup()}
      {dpcHoofdDoc && (
        <>
          <SpacerAtom space={4} />
          <i>{Teksten.createScreen.dpcupload.tekst}</i>
        </>
      )}

      <FormMolecule key={root.formUniqueKey} rightButton={rightButton()} leftButton={leftButton()}>
        {formConfig.fields.required.map((field) => {
          const renderNull = field.type === "custom" && field.render() === null;
          return (
            <>
              <FormFieldRenderer key={field.id} field={field} />
              {!renderNull && <SpacerAtom space={4} />}
            </>
          );
        })}

        <FoldableMolecule label={"Optionele velden"} defaultFolded={false}>
          <SpacerAtom space={4} />

          {formConfig.fields.optional.map((field) => (
            <>
              <FormFieldRenderer key={field.id} field={{ ...field, required: false }} />
              <SpacerAtom space={4} />
            </>
          ))}
        </FoldableMolecule>
      </FormMolecule>
    </>
  );
}
